import { useEffect } from "react";

export type UseCssVarInitializerOptions = {
    elementRef: React.RefObject<HTMLElement | null>;
    cssVarX: string;
    cssVarY: string;
    /**
     * Optional extra dependencies that should retrigger the initialization attempt.
     * Common use: `className` if CSS variables are provided via class-based styles.
     */
    deps?: ReadonlyArray<unknown>;
    /**
     * If true, the hook does nothing.
     * Common use: stop initializing once the user has moved the element.
     */
    shouldSkip?: () => boolean;
    maxAttempts?: number;
    onResolved: (values: { x?: number; y?: number }) => void;
};

export function useCssVarInitializer({
    elementRef,
    cssVarX,
    cssVarY,
    deps = [],
    shouldSkip,
    maxAttempts = 5,
    onResolved,
}: UseCssVarInitializerOptions) {
    useEffect(() => {
        if (shouldSkip?.()) {
            return;
        }

        let rafId: number | null = null;
        let attemptCount = 0;

        const tryResolve = () => {
            if (shouldSkip?.()) {
                return;
            }

            if (!elementRef.current) {
                return;
            }

            const computed = window.getComputedStyle(elementRef.current);
            const rawX = computed.getPropertyValue(cssVarX).trim();
            const rawY = computed.getPropertyValue(cssVarY).trim();

            const parsedX = Number.parseFloat(rawX);
            const parsedY = Number.parseFloat(rawY);

            const hasX = Number.isFinite(parsedX);
            const hasY = Number.isFinite(parsedY);

            if (hasX || hasY) {
                onResolved({
                    x: hasX ? parsedX : undefined,
                    y: hasY ? parsedY : undefined,
                });

                return;
            }

            attemptCount += 1;
            if (attemptCount < maxAttempts) {
                rafId = window.requestAnimationFrame(tryResolve);
            }
        };

        tryResolve();

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
        };
        // Intentionally controlled by explicit `deps` so callers don't need to memoize options.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
